import { useEffect, useState, type FormEvent } from "react";
import { useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { AlertCircle } from "lucide-react";
import { fetchBookingDetails } from "@/services/bookingDetailsService";
import { BookingDetailsView } from "@/components/bookingInfo/BookingDetailsView";
import { DetailsLoader } from "@/components/common/DetailsLoader";

const BOOKING_ID_PATTERN = /^G[A-Za-z0-9]{9}$/;
const BOOKING_ID_ERROR =
  "Booking ID must start with G and be exactly 10 characters long.";

export default function BookingInfo() {
  const [searchParams, setSearchParams] = useSearchParams();
  // The URL is the source of truth: changing `bookingId` (via the form or directly in the
  // address bar) drives the query below and triggers a fresh fetch automatically.
  const activeBookingId = (searchParams.get("bookingId") ?? "").trim();
  const [bookingId, setBookingId] = useState(activeBookingId);
  const [validationError, setValidationError] = useState<string | null>(null);

  const normalizedBookingId = bookingId.trim().toUpperCase();
  const isBookingIdValid = BOOKING_ID_PATTERN.test(normalizedBookingId);

  useEffect(() => {
    setBookingId(activeBookingId);
    setValidationError(null);
  }, [activeBookingId]);

  useEffect(() => {
    const prevTitle = document.title;
    document.title = "Booking Info — Duri Ridesharing";
    return () => {
      document.title = prevTitle;
    };
  }, []);

  const {
    data: booking,
    isLoading,
    isError,
    error,
  } = useQuery({
    queryKey: ["booking-details", activeBookingId],
    queryFn: () =>
      fetchBookingDetails({ bookingId: activeBookingId, context: "guest" }),
    enabled: activeBookingId.length > 0,
    retry: false,
  });

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    if (!normalizedBookingId) return;
    if (!isBookingIdValid) {
      setValidationError(BOOKING_ID_ERROR);
      return;
    }
    setValidationError(null);
    setSearchParams({ bookingId: normalizedBookingId });
  };

  const clearSearch = () => setSearchParams({});

  return (
    <div className="min-h-[80vh] bg-muted/30 px-4 py-12 sm:py-16">
      <div className="mx-auto max-w-2xl">
        {activeBookingId && isLoading ? (
          <DetailsLoader
            title="Fetching your booking"
            subtitle="Hang tight while we pull up your booking details."
          />
        ) : activeBookingId && isError ? (
          <div className="space-y-4">
            <button
              type="button"
              onClick={clearSearch}
              className="text-sm font-semibold text-emerald-700 hover:underline"
            >
              ← Look up another booking
            </button>
            <div className="flex items-start gap-2 rounded-xl border border-destructive/30 bg-destructive/5 p-4 text-sm text-destructive">
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
              <span>
                {(error as Error)?.message ||
                  "We couldn't find that booking. Please check the Booking ID and try again."}
              </span>
            </div>
          </div>
        ) : booking ? (
          <>
            <button
              type="button"
              onClick={clearSearch}
              className="mb-4 text-sm font-semibold text-emerald-700 hover:underline"
            >
              ← Look up another booking
            </button>
            <BookingDetailsView booking={booking} />
          </>
        ) : (
          <>
            <h1 className="text-3xl font-extrabold tracking-tight text-foreground sm:text-4xl">
              Find your booking
            </h1>
            <p className="mt-2 text-sm text-muted-foreground">
              Enter your Booking ID to see full details.
            </p>

            <form onSubmit={handleSubmit} className="mt-6 space-y-3">
              <input
                type="text"
                value={bookingId}
                onChange={(event) => {
                  setBookingId(event.target.value.toUpperCase());
                  if (validationError) setValidationError(null);
                }}
                placeholder="e.g. G25DCS6472"
                aria-label="Booking ID"
                maxLength={10}
                className="h-12 w-full rounded-xl border-2 border-emerald-500 bg-card px-4 text-sm uppercase outline-none transition-shadow focus:ring-2 focus:ring-emerald-200"
              />
              {validationError && (
                <p className="text-sm text-destructive">{validationError}</p>
              )}
              <button
                type="submit"
                disabled={!normalizedBookingId}
                className="flex h-12 w-full items-center justify-center gap-2 rounded-xl bg-emerald-600 text-sm font-bold text-white transition-colors hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-50"
              >
                Get Booking Details
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
